import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const AboutPage = () => {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing" data-testid="about-hero">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
            {/* Image Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <div className="relative sticky top-32">
                <div className="absolute -inset-4 border border-brand-accent/20" />
                <img
                  src="https://customer-assets.emergentagent.com/job_d8772972-5611-4b02-b607-8b27eec91081/artifacts/ozst57pd_Inas%20Headshot.jpg"
                  alt="Inas-Khalidah Mahdi"
                  data-testid="about-headshot"
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
            </motion.div>

            {/* Content Column */}
            <motion.div
              variants={{
                visible: { transition: { staggerChildren: 0.15 } }
              }}
              initial="hidden"
              animate="visible"
              className="lg:col-span-3"
            >
              <motion.div variants={fadeUp} transition={{ duration: 0.6 }}>
                <p className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4">
                  About
                </p>
                <h1 className="font-heading text-4xl md:text-5xl text-text-primary mb-8 leading-tight">
                  Inas-Khalidah Mahdi
                </h1>
              </motion.div>

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className="font-accent text-xl text-text-secondary leading-relaxed mb-8"
              >
                Embodied Researcher. Healing Justice Practitioner. Survivor.
              </motion.p>

              <motion.div
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className="decorative-line mb-8"
              />

              <motion.div
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className="space-y-6 font-body text-text-secondary leading-relaxed"
              >
                <p>
                  I am a survivor of sexual violence, and my journey through healing has become 
                  my life's work. As an embodied researcher and healing justice practitioner, 
                  I dedicate my practice to supporting Black survivors of gender-based violence, 
                  racialized harm, and sexual trauma.
                </p>

                <p>
                  Currently, I am pursuing my doctoral research at{" "}
                  <span className="text-text-primary">Johns Hopkins Bloomberg School of Public Health</span>, 
                  where I study embodied sexology with a particular focus on child sexual abuse survivors. 
                  My research is not separate from my practice — it is deeply woven into it.
                </p>

                <p>
                  Through my own healing journey, I discovered the profound power of somatic practices — 
                  the ability to access sensation, to rebuild relationship with the body, to reclaim 
                  what was taken. This discovery became the foundation for{" "}
                  <span className="text-brand-accent">Somale Restoration Collective</span>.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-spacing bg-bg-secondary" data-testid="about-philosophy">
        <div className="section-container">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-8">
                My Philosophy
              </h2>

              <div className="space-y-6 font-body text-text-secondary leading-relaxed">
                <p>
                  For survivors — especially Black survivors — the body has often been a site of 
                  violation, control, and disconnection. We have been taught to distrust our 
                  sensations, to override our needs, to exist outside of ourselves.
                </p>

                <p className="text-xl font-accent text-text-primary">
                  "The ability to access sensation, to feel safe in the body, is not a luxury — 
                  it is a fundamental human right."
                </p>

                <p>
                  When we are denied somatic access — the ability to inhabit, feel, and trust 
                  our bodies — we are denied a core dimension of our humanity. This is not 
                  merely a personal tragedy; it is a{" "}
                  <span className="text-text-primary">health justice failure</span>.
                </p>

                <p>
                  I believe that embodied, consent-centered practices are not indulgences. 
                  They are public health interventions. They are pathways to restoration. 
                  They are acts of resistance.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-spacing" data-testid="about-mission">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-8">
                The Work
              </h2>

              <div className="space-y-6 font-body text-text-secondary leading-relaxed">
                <p>
                  Through Somale Restoration Collective, I create spaces for Black survivors 
                  to explore trauma-responsive embodiment, sexual healing, and what it means 
                  to come home to the body.
                </p>

                <p>
                  My work spans multiple dimensions: facilitating{" "}
                  <span className="text-text-primary">Somale Circles</span> — community healing 
                  gatherings for Black survivors; offering{" "}
                  <span className="text-text-primary">one-on-one coaching</span> for those 
                  seeking personalized support; developing{" "}
                  <span className="text-text-primary">trainings</span> for providers and 
                  health systems; and providing{" "}
                  <span className="text-text-primary">educational speaking engagements</span>{" "}
                  that bring these conversations to broader audiences.
                </p>

                <p>
                  At the heart of everything I do is a commitment to reclaiming the Black body 
                  as a site of power, sovereignty, and collective liberation.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="card h-full flex flex-col justify-center">
                <p className="font-accent text-2xl text-brand-accent mb-6 leading-relaxed">
                  "Healing is not about returning to who you were before. It is about 
                  becoming who you were always meant to be — fully embodied, 
                  fully present, fully free."
                </p>
                <p className="font-body text-sm text-text-secondary uppercase tracking-wider">
                  — Inas-Khalidah Mahdi
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Connected Initiatives */}
      <section className="section-spacing bg-brand-deep-green" data-testid="about-initiatives">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4">
              A Continuum of Justice
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-8">
              Connected Initiatives
            </h2>

            <p className="font-body text-text-secondary leading-relaxed mb-8">
              Somale Restoration Collective exists within a broader ecosystem of reproductive 
              justice work. Together with Nzingha Maternal Health and the Collective for 
              Cross-Diasporan Reproductive Justice (CCDRJ), we form a continuum — from 
              systemic transformation to narrative change to embodied healing.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 border border-white/10">
                <h3 className="font-heading text-lg text-text-primary mb-3">
                  Nzingha Maternal Health
                </h3>
                <p className="font-body text-sm text-text-secondary mb-4">
                  Transforming maternal health systems
                </p>
                <a
                  href="https://www.nzinghamaternalhealth.org/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-nzingha-link"
                  className="text-brand-accent text-sm hover:underline"
                >
                  Visit Website →
                </a>
              </div>

              <div className="p-6 border border-white/10">
                <h3 className="font-heading text-lg text-text-primary mb-3">
                  CCDRJ
                </h3>
                <p className="font-body text-sm text-text-secondary">
                  Shifting narratives across the diaspora
                </p>
              </div>

              <div className="p-6 border border-brand-accent/30 bg-white/5">
                <h3 className="font-heading text-lg text-brand-accent mb-3">
                  Somale Restoration
                </h3>
                <p className="font-body text-sm text-text-secondary">
                  Healing bodies, restoring sovereignty
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing" data-testid="about-cta">
        <div className="section-container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-6">
              Ready to Connect?
            </h2>
            <p className="font-body text-text-secondary max-w-2xl mx-auto mb-8">
              I welcome you to explore my offerings or book a consultation to discuss 
              how we might work together on your healing journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/offerings" data-testid="about-cta-offerings" className="btn-secondary">
                View Offerings
              </Link>
              <Link to="/book" data-testid="about-cta-book" className="btn-primary">
                Book a Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
